import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import MarketingLayout, { useMLang } from "./MarketingLayout";

const PRICE = {
    en: {
        title: "Honest pricing. No card-on-file required.",
        sub: "One-time setup, then a flat monthly fee. Cancel anytime after the 14-day trial.",
        setupNote: "+ $299 one-time setup · 14-day free trial",
        ctaTrial: "Start 14-day trial",
        comingSoon: " — coming soon",
        faqTitle: "Pricing FAQ",
        faqs: [
            { q: "What does the $299 setup cover?", a: "We import your menu, configure colors and branding, write your bilingual hero copy, generate AI photos for up to 12 dishes, and walk you through the admin in a 30-min onboarding call." },
            { q: "Can I cancel anytime?", a: "Yes. After the 14-day trial there's no contract; cancel from the billing portal and your storefront keeps running through the end of the paid period." },
            { q: "Do I need a Stripe account?", a: "No. You collect from customers via WhatsApp + Lynk / Tpago / PayPal / cash. The $99 or $189/mo only flows from you to us (via Stripe or off-Stripe — we accept manual receipts)." },
            { q: "What if I want a custom domain?", a: "Available on Pro after a 1-time $99 DNS-setup fee. Roadmap." },
        ],
        plans: [
            {
                slug: "starter",
                name: "Basic",
                price: 99,
                bullets: ["Bilingual storefront", "WhatsApp checkout", "Loyalty stamps", "Testimonials", "Mobile-friendly admin"],
            },
            {
                slug: "pro",
                name: "Pro",
                price: 189,
                featured: true,
                bullets: ["Everything in Basic", "Catering flow + Calendar", "AI menu photos (Gemini)", "Email broadcasts", "Profitability dashboard", "Order archive & wipe"],
            },
        ],
    },
    es: {
        title: "Precios honestos. Sin tarjeta requerida.",
        sub: "Pago único de instalación, luego una mensualidad fija. Cancela cuando quieras después de la prueba de 14 días.",
        setupNote: "+ $299 setup único · 14 días de prueba gratis",
        ctaTrial: "Empieza la prueba de 14 días",
        comingSoon: " — próximamente",
        faqTitle: "Preguntas sobre precios",
        faqs: [
            { q: "¿Qué cubre el setup de $299?", a: "Importamos tu menú, configuramos colores y branding, escribimos tu texto bilingüe, generamos fotos IA para hasta 12 platos, y te guiamos en una llamada de 30 min." },
            { q: "¿Puedo cancelar cuando quiera?", a: "Sí. Después de los 14 días no hay contrato; cancelas desde el portal y tu sitio sigue corriendo hasta el final del periodo pagado." },
            { q: "¿Necesito una cuenta Stripe?", a: "No. Tú cobras a los clientes por WhatsApp + Lynk / Tpago / PayPal / efectivo. Los $99 o $189/mes solo fluyen de ti a nosotros (vía Stripe o manual — aceptamos recibos manuales)." },
            { q: "¿Y si quiero un dominio propio?", a: "Disponible en Pro con un setup único de $99 para DNS. En el roadmap." },
        ],
        plans: [
            {
                slug: "starter",
                name: "Básico",
                price: 99,
                bullets: ["Tienda bilingüe", "Pago por WhatsApp", "Sellos de lealtad", "Testimonios", "Admin desde el móvil"],
            },
            {
                slug: "pro",
                name: "Pro",
                price: 189,
                featured: true,
                bullets: ["Todo de Básico", "Catering + Calendario", "Fotos IA del menú (Gemini)", "Envíos de correo masivos", "Panel de rentabilidad", "Archivo y limpieza de pedidos"],
            },
        ],
    },
};

const MarketingPricing = () => {
    const { lang } = useMLang();
    const c = PRICE[lang];
    return (
        <MarketingLayout testid="marketing-pricing">
            <section className="max-w-4xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-12 text-center">
                <h1 className="font-serif font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">{c.title}</h1>
                <p className="text-white/60 mt-5 max-w-2xl mx-auto">{c.sub}</p>
            </section>

            <section className="max-w-3xl mx-auto px-6 sm:px-10 pb-16 grid md:grid-cols-2 gap-5">
                {c.plans.map((p) => (
                    <div
                        key={p.slug}
                        data-testid={`pricing-plan-${p.slug}`}
                        className={`p-7 rounded-2xl border ${p.featured ? "border-orange-400/50 bg-orange-500/[0.07] ring-1 ring-orange-400/30" : "border-white/10 bg-white/[0.03]"}`}
                    >
                        <div className="flex items-baseline justify-between">
                            <h3 className="font-serif font-bold text-2xl">{p.name}</h3>
                            {p.featured && <span className="text-[10px] uppercase tracking-widest font-bold text-orange-300">{lang === "es" ? "Más popular" : "Most popular"}</span>}
                        </div>
                        <div className="mt-4 flex items-baseline gap-1">
                            <span className="text-5xl font-serif font-black">${p.price}</span>
                            <span className="text-white/50">/{lang === "es" ? "mes" : "month"}</span>
                        </div>
                        <div className="text-xs text-white/50 mt-1">{c.setupNote}</div>
                        <ul className="mt-6 space-y-2 text-sm text-white/85">
                            {p.bullets.map((b) => (
                                <li key={b} className="flex items-start gap-2"><Check className="w-4 h-4 text-orange-300 mt-0.5 shrink-0" /> {b}</li>
                            ))}
                        </ul>
                        <Link
                            to={`/heavenly/signup?plan=${p.slug}`}
                            data-testid={`pricing-cta-${p.slug}`}
                            className={`mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold transition-colors ${p.featured ? "bg-orange-500 hover:bg-orange-400 text-white" : "bg-white text-black hover:bg-white/90"}`}
                        >
                            {c.ctaTrial}
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                ))}
            </section>

            <section className="max-w-3xl mx-auto px-6 sm:px-10 pb-24">
                <h2 className="font-serif font-bold text-2xl sm:text-3xl mb-7">{c.faqTitle}</h2>
                <div className="space-y-4">
                    {c.faqs.map((f) => (
                        <div
                            key={f.q}
                            data-testid={`pricing-faq-${f.q.slice(0,30)}`}
                            className="p-5 rounded-xl border border-white/10 bg-white/[0.03]"
                        >
                            <div className="font-bold text-white mb-1.5">{f.q}</div>
                            <div className="text-sm text-white/65 leading-relaxed">{f.a}</div>
                        </div>
                    ))}
                </div>
                <div className="mt-12 flex justify-center">
                    <Link to="/r/delicias" className="inline-flex items-center gap-2 text-orange-300 hover:text-orange-200 font-semibold">
                        {lang === "es" ? "Ver el demo en vivo" : "See the live demo"}
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </MarketingLayout>
    );
};

export default MarketingPricing;
